import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatPrice, formatDuration, AIRLINE_LOGOS, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, Lock, CalendarBlank, Armchair } from "@phosphor-icons/react";
import SeatsAndExtras from "@/components/booking/SeatsAndExtras";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSEO } from "@/lib/seo";

const COUNTRIES = [
  { v: "US", l: "United States" },
  { v: "CA", l: "Canada" },
  { v: "GB", l: "United Kingdom" },
  { v: "AU", l: "Australia" },
  { v: "DE", l: "Germany" },
  { v: "FR", l: "France" },
  { v: "ES", l: "Spain" },
  { v: "IT", l: "Italy" },
  { v: "NL", l: "Netherlands" },
  { v: "JP", l: "Japan" },
  { v: "SG", l: "Singapore" },
  { v: "IN", l: "India" },
  { v: "BR", l: "Brazil" },
  { v: "MX", l: "Mexico" },
  { v: "AE", l: "United Arab Emirates" },
  { v: "OTHER", l: "Other" },
];

export function Stepper({ step, onStepClick }) {
  const steps = ["Itinerary", "Passengers", "Seats & Extras", "Payment", "Confirmation"];
  // Only steps 0-2 are clickable when a handler is provided. Payment (3) and
  // Confirmation (4) advance via explicit user action (Stripe redirect / post-
  // payment landing) so we don't expose them as jump targets.
  const MAX_CLICKABLE = 2;
  return (
    <div
      className="flex items-center gap-2 md:gap-3 mb-8 md:mb-10 overflow-x-auto no-scrollbar -mx-1 px-1"
      data-testid="stepper"
    >
      {steps.map((s, i) => {
        const clickable = !!onStepClick && i <= MAX_CLICKABLE && i !== step;
        const inner = (
          <>
            <div className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center font-semibold text-xs md:text-sm shrink-0 ${i <= step ? "bg-[#C29B62] text-[#0A0A0B]" : "bg-[#E0DDD6] text-[#666460]"}`}>
              {i + 1}
            </div>
            <span
              className={`text-[10px] md:text-xs tracking-[0.14em] md:tracking-[0.16em] uppercase whitespace-nowrap ${
                i === step ? "text-[#1C1C1C] font-semibold" : "text-[#666460]"
              }`}
            >
              {s}
            </span>
          </>
        );
        return (
          <div key={s} className="flex items-center gap-2 md:gap-3 shrink-0">
            {clickable ? (
              <button
                type="button"
                onClick={() => onStepClick(i)}
                data-testid={`stepper-step-${i}`}
                className="flex items-center gap-2 md:gap-3 shrink-0 transition-opacity hover:opacity-80 focus:outline-none focus-visible:opacity-80"
              >
                {inner}
              </button>
            ) : (
              <div className="flex items-center gap-2 md:gap-3 shrink-0">
                {inner}
              </div>
            )}
            {i < steps.length - 1 && <div className="w-5 md:w-8 h-px bg-[#E0DDD6] shrink-0" />}
          </div>
        );
      })}
    </div>
  );
}

// Reusable itinerary leg card — uses the same fields stored from /search.
function ItineraryLeg({ leg, label, date }) {
  return (
    <div className="bg-white border border-[#E0DDD6] p-5 md:p-6" data-testid={`itinerary-leg-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-4">
        <p className="tcp-overline text-[#C29B62]">{label}</p>
        <p className="text-xs text-[#666460]">{date}</p>
      </div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <p className="font-serif text-3xl text-[#1C1C1C] tracking-tight">{leg.depart_time}</p>
          <p className="text-xs text-[#666460] mt-1">{leg.origin}</p>
        </div>
        <div className="flex-1 px-3">
          <p className="text-[10px] text-[#666460] text-center tracking-[0.16em] uppercase mb-1">
            {leg.duration_minutes ? `${Math.floor(leg.duration_minutes/60)}h ${leg.duration_minutes%60}m` : ""}
          </p>
          <div className="h-px bg-[#E0DDD6]" />
          <p className="text-[10px] text-[#666460] text-center mt-1">{leg.stops === 0 ? "Non-stop" : `${leg.stops} stop`}</p>
        </div>
        <div className="text-right">
          <p className="font-serif text-3xl text-[#1C1C1C] tracking-tight">{leg.arrive_time}</p>
          <p className="text-xs text-[#666460] mt-1">{leg.destination}</p>
        </div>
      </div>
      <div className="pt-3 border-t border-[#E0DDD6] grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-[#666460]">
        <div><span className="text-[10px] tracking-[0.18em] uppercase block mb-0.5">Airline</span><span className="text-[#1C1C1C]">{leg.airline_name}</span></div>
        <div><span className="text-[10px] tracking-[0.18em] uppercase block mb-0.5">Flight</span><span className="text-[#1C1C1C]">{leg.flight_number}</span></div>
        <div><span className="text-[10px] tracking-[0.18em] uppercase block mb-0.5">Cabin</span><span className="text-[#1C1C1C] capitalize">{(leg.cabin || "").replace("_", " ")}</span></div>
        <div><span className="text-[10px] tracking-[0.18em] uppercase block mb-0.5">Baggage</span><span className="text-[#1C1C1C]">{leg.baggage_summary || "Included"}</span></div>
      </div>
    </div>
  );
}

export default function BookingFlow() {
  const nav = useNavigate();
  const [flight, setFlight] = useState(null);
  const [step, setStep] = useState(0);
  const [pax, setPax] = useState([]);
  const [contact, setContact] = useState({ email: "", phone: "" });
  const [extras, setExtras] = useState({ services: [], selected_seats: [], extras_total: 0 });
  const [submitting, setSubmitting] = useState(false);

  useSEO({ title: "Booking | TheCityPair", noindex: true });

  useEffect(() => {
    const raw = sessionStorage.getItem("tcp_selected_flight");
    if (!raw) { nav("/"); return; }
    const f = JSON.parse(raw);
    setFlight(f);
    const br = f.pax_breakdown || { adults: f.passengers || 1, children: 0, infants: 0 };
    // Build typed passenger seeds: N adults, then children, then infants.
    const seeds = [];
    for (let i = 0; i < (br.adults || 1); i++) seeds.push({ type: "adult" });
    for (let i = 0; i < (br.children || 0); i++) seeds.push({ type: "child" });
    for (let i = 0; i < (br.infants || 0); i++) seeds.push({ type: "infant_without_seat" });
    setPax(seeds.map((s) => ({
      ...s,
      first_name: "", last_name: "", dob: "", gender: "M",
      nationality: "US", passport_number: "",
    })));
  }, [nav]);

  const handleExtrasChange = useCallback((next) => setExtras(next), []);

  if (!flight) return null;

  const update = (i, k, v) => setPax(p => p.map((x, idx) => idx === i ? { ...x, [k]: v } : x));

  const next = () => {
    // step 0 is Itinerary review — no required fields
    if (step === 1) {
      for (const p of pax) {
        if (!p.first_name || !p.last_name || !p.dob) { toast.error("Please complete all passenger fields."); return; }
      }
      if (!contact.email || !contact.phone) { toast.error("Please complete contact info."); return; }
    }
    // step 2 is Seats & Extras — no required fields
    // step 3 is Payment — handled by submit() (Stripe redirect)
    setStep(s => Math.min(s + 1, 3));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      // Step 1: Persist a pending booking (price_total is the amount we'll
      // charge in Stripe). The backend gates payment via the booking_id.
      const { data: booking } = await api.post("/bookings", {
        product_type: "flights",
        flight_id: flight.id,
        origin: flight.origin, destination: flight.destination,
        depart_date: flight.depart_date, return_date: flight.return_date,
        cabin: flight.cabin, airline: flight.airline_name,
        airline_code: flight.airline_code,
        price_total: (flight.price * pax.length) + (extras.extras_total || 0),
        price: flight.price,
        extras_total: extras.extras_total || 0,
        currency: flight.currency,
        passengers: pax, contact_email: contact.email, contact_phone: contact.phone,
        services: extras.services || [],
        selected_seats: extras.selected_seats || [],
        payment_status: "pending",
      });
      sessionStorage.removeItem("tcp_selected_flight");

      // Step 2: Open a Stripe Checkout Session. Backend derives the amount
      // from the booking record — frontend never sends prices.
      const { data: session } = await api.post("/payments/checkout/session", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      // Step 3: Redirect to Stripe-hosted checkout.
      window.location.href = session.url;
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Booking failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F7F5F0] min-h-screen">
      <div className="bg-[#0A0A0B] relative"><Navbar dark /><div className="h-24" /></div>

      {/* Stepper sits above the grid so the summary aside aligns flush with
          "Your itinerary" on the left at the top of the grid.
          Steps 1-3 are click-jumpable; passenger/contact/extras state is
          preserved in component state so values survive back-and-forth. */}
      <div className="px-6 md:px-12 lg:px-24 pt-10">
        <Stepper
          step={step}
          onStepClick={(i) => {
            setStep(i);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      </div>

      <div className="px-6 md:px-12 lg:px-24 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          {step === 0 && (
            <div className="space-y-6" data-testid="step-itinerary">
              <div>
                <h2 className="font-serif text-4xl text-[#1C1C1C] tracking-tighter">Your itinerary</h2>
                <p className="text-sm text-[#666460] mt-2">Review both legs before we collect passenger details.</p>
              </div>
              <ItineraryLeg leg={flight} label="Outbound" date={flight.depart_date} />
              {flight.return_leg && (
                <ItineraryLeg leg={flight.return_leg} label="Return" date={flight.return_date} />
              )}
              {/* On desktop the Price Details aside on the right shows the
                  fare breakdown, so this dark "Total fare" footer is hidden
                  to avoid duplication. It stays visible on mobile/tablet
                  where there's no aside. */}
              <div className="lg:hidden bg-[#0A0A0B] text-[#F7F5F0] p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <p className="tcp-overline text-[#C29B62] mb-1">Total fare</p>
                  <p className="font-serif text-3xl tracking-tight">
                    {flight.currency || "USD"} {Number(flight.price).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-[#A09E9A] mt-1">
                    {flight.return_leg
                      ? `${flight.passengers || 1} traveler · round-trip · taxes & fees included`
                      : `${flight.passengers || 1} traveler · one-way · taxes & fees included`}
                  </p>
                </div>
                <p className="text-xs tracking-[0.16em] uppercase text-[#A09E9A]">
                  Continue to passenger details →
                </p>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-8" data-testid="step-passengers">
              <h2 className="font-serif text-4xl text-[#1C1C1C] tracking-tighter">Passenger Details</h2>
              <p className="text-sm text-[#666460] -mt-4">Enter names exactly as they appear on government-issued ID.</p>
              {pax.map((p, i) => {
                const typeLabel =
                  p.type === "child" ? "Child"
                  : p.type === "infant_without_seat" ? "Infant (lap)"
                  : "Adult";
                const sameTypeBefore = pax.slice(0, i).filter((x) => x.type === p.type).length;
                const num = sameTypeBefore + 1;
                // Composite key — pax list is built once from the offer and
                // never reorders/inserts mid-flow, but we still avoid bare
                // index keys so React always reconciles fields correctly.
                const paxKey = `${p.type}-${num}`;
                return (
                <div key={paxKey} className="bg-white border border-[#E0DDD6] p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 flex items-center justify-between">
                    <p className="tcp-overline text-[#C29B62]" data-testid={`pax-${i}-type`}>
                      {typeLabel} {num}
                    </p>
                    <p className="text-[10px] text-[#A09E9A] uppercase tracking-wider">
                      Passenger {i + 1} of {pax.length}
                    </p>
                  </div>
                  <div><label className="tcp-label">First Name</label><input className="tcp-input" data-testid={`pax-${i}-first`} value={p.first_name} onChange={(e) => update(i, "first_name", e.target.value)} /></div>
                  <div><label className="tcp-label">Last Name</label><input className="tcp-input" data-testid={`pax-${i}-last`} value={p.last_name} onChange={(e) => update(i, "last_name", e.target.value)} /></div>
                  <div>
                    <label className="tcp-label">Date of Birth</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid={`pax-${i}-dob`}
                          className="tcp-input flex items-center justify-between text-left"
                        >
                          <span className={p.dob ? "text-[#1C1C1C]" : "text-[#A09E9A]"}>
                            {p.dob ? format(new Date(p.dob + "T00:00:00"), "MMM d, yyyy") : "Select date"}
                          </span>
                          <CalendarBlank size={16} className="text-[#C29B62]" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="p-0 w-auto">
                        <Calendar
                          mode="single"
                          captionLayout="dropdown-buttons"
                          fromYear={1920}
                          toYear={new Date().getFullYear()}
                          defaultMonth={p.dob ? new Date(p.dob + "T00:00:00") : new Date(new Date().getFullYear() - 30, 0, 1)}
                          selected={p.dob ? new Date(p.dob + "T00:00:00") : undefined}
                          onSelect={(d) => d && update(i, "dob", format(d, "yyyy-MM-dd"))}
                          disabled={(d) => d > new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <label className="tcp-label">Nationality</label>
                    <Select value={p.nationality} onValueChange={(v) => update(i, "nationality", v)}>
                      <SelectTrigger className="tcp-input h-auto" data-testid={`pax-${i}-nat`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-[260px]">
                        {COUNTRIES.map((c) => (
                          <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="tcp-label">Gender</label>
                    <Select value={p.gender} onValueChange={(v) => update(i, "gender", v)}>
                      <SelectTrigger className="tcp-input h-auto" data-testid={`pax-${i}-gender`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M">Male</SelectItem>
                        <SelectItem value="F">Female</SelectItem>
                        <SelectItem value="X">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><label className="tcp-label">Passport Number (optional)</label><input className="tcp-input" data-testid={`pax-${i}-passport`} value={p.passport_number} onChange={(e) => update(i, "passport_number", e.target.value)} /></div>
                </div>
                );
              })}

              {/* Single contact block — applies to the whole booking (primary
                  traveler). Captured once, regardless of passenger count. */}
              <div className="bg-white border border-[#E0DDD6] p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-block">
                <div className="md:col-span-2">
                  <p className="tcp-overline text-[#C29B62]">Contact Details</p>
                  <p className="text-xs text-[#666460] mt-1">We'll send your booking confirmation and any flight updates here.</p>
                </div>
                <div>
                  <label className="tcp-label">Contact Number</label>
                  <input className="tcp-input" data-testid="contact-phone" value={contact.phone} onChange={(e) => setContact({...contact, phone: e.target.value})} placeholder="+1 (555) 123-4567" />
                </div>
                <div>
                  <label className="tcp-label">Email Address</label>
                  <input type="email" className="tcp-input" data-testid="contact-email" value={contact.email} onChange={(e) => setContact({...contact, email: e.target.value})} placeholder="you@example.com" />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-2" data-testid="step-extras">
              <h2 className="font-serif text-4xl text-[#1C1C1C] tracking-tighter mb-4">Seats &amp; Extras</h2>
              <p className="text-sm text-[#666460] mb-6">Pick your seats and add extra bags. Fare rules are shown below — review them before continuing.</p>
              <SeatsAndExtras
                offerId={flight.id}
                paxList={pax}
                onUpdateExtras={handleExtrasChange}
              />
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6" data-testid="step-payment">
              <h2 className="font-serif text-4xl text-[#1C1C1C] tracking-tighter">Payment</h2>
              <p className="text-sm text-[#666460] -mt-4">
                Your card details are entered securely on Stripe. We never see or store your card number.
              </p>

              <div className="bg-white border border-[#E0DDD6] p-6 space-y-4" data-testid="payment-summary">
                <div className="flex items-center justify-between">
                  <p className="tcp-overline text-[#C29B62]">Order Summary</p>
                  <p className="text-xs text-[#666460] flex items-center gap-1"><Lock size={12} /> 256-bit TLS · Stripe-secured</p>
                </div>
                <div className="space-y-2 text-sm pt-2 border-t border-[#E0DDD6]">
                  <div className="flex justify-between text-[#666460]">
                    <span>{flight.origin} → {flight.destination}{flight.return_leg ? ` → ${flight.origin}` : ""}</span>
                    <span>{flight.depart_date}{flight.return_date ? ` · ${flight.return_date}` : ""}</span>
                  </div>
                  <div className="flex justify-between text-[#666460]">
                    <span>{flight.return_leg ? "Round-trip fare" : "Fare"} × {pax.length}</span>
                    <span>{formatPrice(flight.price * pax.length, flight.currency)}</span>
                  </div>
                  {extras.extras_total > 0 && (
                    <div className="flex justify-between text-[#666460]">
                      <span>Seats &amp; baggage</span>
                      <span>+ {formatPrice(extras.extras_total, flight.currency)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-[#666460]">
                    <span>Taxes &amp; fees</span>
                    <span>Included</span>
                  </div>
                </div>
                <div className="flex justify-between items-end pt-4 border-t border-[#E0DDD6]">
                  <span className="tcp-overline text-[#666460]">Total Due</span>
                  <span className="font-serif text-3xl" style={{ color: "#C29B62" }} data-testid="payment-total">
                    {formatPrice((flight.price * pax.length) + (extras.extras_total || 0), flight.currency)}
                  </span>
                </div>
              </div>

              <div className="bg-[#0A0A0B] text-[#F7F5F0] p-6">
                <p className="tcp-overline text-[#C29B62] mb-3">Next: Secure Payment</p>
                <p className="text-sm leading-relaxed text-[#A09E9A] mb-4">
                  Clicking <em>"Pay with Stripe"</em> will take you to Stripe's PCI-compliant checkout where you'll enter your card details. We support all major cards including Visa, Mastercard, American Express, and Discover.
                </p>
                <div className="flex items-center gap-2 text-xs text-[#A09E9A]">
                  <CheckCircle size={14} className="text-[#C29B62]" />
                  <span>Free cancellation within 24 hours</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-10">
            <button onClick={() => step === 0 ? nav(-1) : setStep(s => s - 1)} className="text-sm tracking-wider uppercase text-[#666460] hover:text-[#1C1C1C]" data-testid="back-btn">← Back</button>
            {step < 3
              ? <button onClick={next} className="btn-gold text-xs tracking-[0.16em] uppercase" data-testid="next-btn">Continue</button>
              : <button onClick={submit} disabled={submitting} className="btn-gold text-xs tracking-[0.16em] uppercase" data-testid="confirm-btn">{submitting ? "Redirecting..." : "Pay with Stripe →"}</button>}
          </div>
        </div>

        {/* Summary sticky — desktop only. On mobile/tablet the aside would
            stack below the step content and just duplicate the itinerary,
            adding visual noise without value.
            ── Step-aware content ──
            • Step 0 (Itinerary): clean Price Details (USD) table — flight
              cards are already shown on the left, so the aside is purely
              pricing.
            • Steps 1-3: compact flight-leg reminder + price breakdown, since
              the left pane no longer shows the itinerary. */}
        <aside className="hidden lg:block lg:col-span-4">
          {step === 0 ? (
            // Push the aside down by the height of the "Your itinerary" h2 +
            // subtitle on the left so the Price Details card starts flush
            // with the top of the Outbound flight card.
            <div className="bg-white border border-[#E0DDD6] p-7 sticky top-24 lg:mt-[88px]" data-testid="price-details">
              <p className="font-serif text-2xl text-[#1C1C1C] tracking-tight mb-1">Price Details <span className="text-[#A09E9A] text-base font-sans">(USD)</span></p>
              <div className="h-px bg-[#E0DDD6] my-5" />

              {/* Header row */}
              <div className="grid grid-cols-12 gap-3 text-[10px] tracking-[0.16em] uppercase text-[#666460] pb-3 border-b border-[#E0DDD6]">
                <div className="col-span-5">Fare / Fee</div>
                <div className="col-span-4 text-right">Price / person</div>
                <div className="col-span-3 text-right">Subtotal</div>
              </div>

              {/* Per-passenger-type rows. Build a count of each type seen.
                  Base fare and taxes shown separately using `tax_ratio` that
                  the backend passes through from Duffel (display-only). */}
              {(() => {
                const breakdown = flight.pax_breakdown || { adults: flight.passengers || 1, children: 0, infants: 0 };
                const perPersonTotal = Number(flight.price) || 0;
                // tax_ratio is the share of the total that is taxes (0–1).
                // Frontend-only split — does not affect the amount charged.
                const taxRatio = Math.max(0, Math.min(1, Number(flight.tax_ratio) || 0));
                const perPersonTax = +(perPersonTotal * taxRatio).toFixed(2);
                const perPersonBase = +(perPersonTotal - perPersonTax).toFixed(2);
                const rows = [];
                if (breakdown.adults > 0) {
                  rows.push({ label: `Adult (x${breakdown.adults})`, qty: breakdown.adults, perPerson: perPersonBase });
                }
                if (breakdown.children > 0) {
                  rows.push({ label: `Child (x${breakdown.children})`, qty: breakdown.children, perPerson: perPersonBase });
                }
                if (breakdown.infants > 0) {
                  rows.push({ label: `Infant (x${breakdown.infants})`, qty: breakdown.infants, perPerson: 0 });
                }
                const totalPax = (breakdown.adults || 0) + (breakdown.children || 0);
                const totalTax = +(perPersonTax * totalPax).toFixed(2);
                return (
                  <>
                    {rows.map((r) => (
                      <div key={r.label} className="grid grid-cols-12 gap-3 py-3 text-sm border-b border-[#F0EDE5]" data-testid={`price-row-${r.label.toLowerCase().split(' ')[0]}`}>
                        <div className="col-span-5 text-[#1C1C1C]">{r.label}</div>
                        <div className="col-span-4 text-right text-[#666460]">{formatPrice(r.perPerson, flight.currency)}</div>
                        <div className="col-span-3 text-right text-[#1C1C1C] font-semibold">{formatPrice(r.perPerson * r.qty, flight.currency)}</div>
                      </div>
                    ))}
                    <div className="grid grid-cols-12 gap-3 py-3 text-sm border-b border-[#F0EDE5]" data-testid="price-row-taxes">
                      <div className="col-span-5 text-[#1C1C1C]">Taxes &amp; fees</div>
                      <div className="col-span-4 text-right text-[#666460]">{totalTax > 0 ? formatPrice(perPersonTax, flight.currency) : "—"}</div>
                      <div className="col-span-3 text-right text-[#1C1C1C] font-semibold">{totalTax > 0 ? formatPrice(totalTax, flight.currency) : "Included"}</div>
                    </div>
                  </>
                );
              })()}

              {/* Optional add-ons (seats + baggage) appear only when picked
                  later in the flow. The aside auto-updates because everything
                  is derived from React state. */}
              {extras.extras_total > 0 && (
                <div className="grid grid-cols-12 gap-3 py-3 text-sm border-b border-[#F0EDE5]" data-testid="price-row-extras">
                  <div className="col-span-5 text-[#1C1C1C]">Seats &amp; baggage</div>
                  <div className="col-span-4 text-right text-[#666460]">—</div>
                  <div className="col-span-3 text-right text-[#1C1C1C] font-semibold">+ {formatPrice(extras.extras_total, flight.currency)}</div>
                </div>
              )}

              {/* Total — emphasised in the serif gold style consistent with
                  the rest of the site. The total is computed from the
                  per-passenger base + tax rows so the math always adds up
                  exactly to what's shown above (no rounding drift). */}
              {(() => {
                const breakdown = flight.pax_breakdown || { adults: flight.passengers || 1, children: 0, infants: 0 };
                const perPersonTotal = Number(flight.price) || 0;
                const taxRatio = Math.max(0, Math.min(1, Number(flight.tax_ratio) || 0));
                const perPersonTax = +(perPersonTotal * taxRatio).toFixed(2);
                const perPersonBase = +(perPersonTotal - perPersonTax).toFixed(2);
                const totalPax = (breakdown.adults || 0) + (breakdown.children || 0);
                const totalBase = +(perPersonBase * totalPax).toFixed(2);
                const totalTax = +(perPersonTax * totalPax).toFixed(2);
                const total = +(totalBase + totalTax + (extras.extras_total || 0)).toFixed(2);
                return (
                  <div className="flex justify-between items-end pt-5 mt-1">
                    <div>
                      <p className="text-sm font-semibold text-[#1C1C1C]">Total Price <span className="text-[#A09E9A] font-normal">(USD)</span></p>
                      <p className="text-[11px] text-[#666460] mt-1">All taxes &amp; fees included</p>
                    </div>
                    <span className="font-serif text-3xl" style={{ color: "#C29B62" }} data-testid="total-price">
                      {formatPrice(total, flight.currency)}
                    </span>
                  </div>
                );
              })()}

              <div className="h-px bg-[#E0DDD6] my-5" />
              <p className="text-xs text-[#666460] flex items-center gap-1"><CheckCircle size={14} className="text-[#C29B62]" /> Free cancellation within 24 hours</p>
            </div>
          ) : (
          <div className="bg-white border border-[#E0DDD6] p-6 sticky top-24" data-testid="booking-summary">
            {/* Outbound leg */}
            <div data-testid="summary-outbound" className="pb-4 border-b border-[#E0DDD6]">
              <div className="flex items-center justify-between mb-3">
                <p className="tcp-overline text-[#C29B62]">Outbound</p>
                <p className="text-[10px] text-[#666460] uppercase tracking-wider">{flight.depart_date}</p>
              </div>
              <div className="flex items-center gap-3 mb-3">
                <img src={AIRLINE_LOGOS[flight.airline_code]} alt={flight.airline_name} className="w-9 h-9 object-contain shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#1C1C1C] truncate">{flight.airline_name}</p>
                  <p className="text-[11px] text-[#666460]">{flight.flight_number} · {flight.cabin}</p>
                </div>
              </div>
              <p className="font-serif text-2xl text-[#1C1C1C] tracking-tight">{flight.origin} → {flight.destination}</p>
              <p className="text-sm text-[#1C1C1C] mt-1.5">{flight.depart_time} → {flight.arrive_time}</p>
              <p className="text-[11px] text-[#666460]">
                {formatDuration(flight.duration_minutes)} · {flight.stops === 0 ? "Non-stop" : `${flight.stops} stop`}
              </p>
            </div>

            {/* Return leg — appears only on round-trip selections */}
            {flight.return_leg && (
              <div data-testid="summary-return" className="py-4 border-b border-[#E0DDD6]">
                <div className="flex items-center justify-between mb-3">
                  <p className="tcp-overline text-[#C29B62]">Return</p>
                  <p className="text-[10px] text-[#666460] uppercase tracking-wider">{flight.return_date}</p>
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <img src={AIRLINE_LOGOS[flight.return_leg.airline_code]} alt={flight.return_leg.airline_name} className="w-9 h-9 object-contain shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#1C1C1C] truncate">{flight.return_leg.airline_name}</p>
                    <p className="text-[11px] text-[#666460]">{flight.return_leg.flight_number} · {flight.return_leg.cabin}</p>
                  </div>
                </div>
                <p className="font-serif text-2xl text-[#1C1C1C] tracking-tight">{flight.return_leg.origin} → {flight.return_leg.destination}</p>
                <p className="text-sm text-[#1C1C1C] mt-1.5">{flight.return_leg.depart_time} → {flight.return_leg.arrive_time}</p>
                <p className="text-[11px] text-[#666460]">
                  {formatDuration(flight.return_leg.duration_minutes)} · {flight.return_leg.stops === 0 ? "Non-stop" : `${flight.return_leg.stops} stop`}
                </p>
              </div>
            )}

            <div className="py-4 space-y-2 text-sm">
              <div className="flex justify-between text-[#666460]">
                <span>{flight.return_leg ? "Round-trip fare" : "Fare"} × {pax.length}</span>
                <span>{formatPrice(flight.price * pax.length, flight.currency)}</span>
              </div>
              <div className="flex justify-between text-[#666460]"><span>Taxes &amp; fees</span><span>Included</span></div>
              {extras.selected_seats?.length > 0 && (
                <div className="flex items-center gap-2 text-[#1C1C1C]" data-testid="seats-badge">
                  <Armchair size={14} className="text-[#C29B62]" />
                  <span>{extras.selected_seats.length} seat{extras.selected_seats.length > 1 ? "s" : ""} selected</span>
                  <span className="ml-auto text-[#666460]">
                    {extras.selected_seats.map((s) => s.designator).join(", ")}
                  </span>
                </div>
              )}
              {extras.extras_total > 0 && (
                <div className="flex justify-between text-[#1C1C1C]" data-testid="extras-line"><span>Seats &amp; baggage</span><span>+ {formatPrice(extras.extras_total, flight.currency)}</span></div>
              )}
            </div>
            <div className="flex justify-between items-end pt-4 border-t border-[#E0DDD6]">
              <span className="tcp-overline text-[#666460]">Total</span>
              <span className="font-serif text-3xl" style={{ color: "#C29B62" }} data-testid="total-price-aside">{formatPrice((flight.price * pax.length) + (extras.extras_total || 0), flight.currency)}</span>
            </div>
            <p className="text-xs text-[#666460] mt-4 flex items-center gap-1"><CheckCircle size={14} className="text-[#C29B62]" /> Free cancellation within 24 hours</p>
          </div>
          )}
        </aside>
      </div>
      <Footer />
    </div>
  );
}
